/**
 * Global Enum Definitions
 *
 * This file consolidates all enum types found across the project.
 * It serves as the single source of truth for shared types.
 * (Syncs with backend/src/types/enums.ts)
 */

// ==========================================
// Shared/Common Enums
// ==========================================

export type InstType = 'FUTURES' | 'SPOT' | 'SWAP';

export const BAR_INTERVAL = {
  M1: '1m',
  M5: '5m',
  M15: '15m',
  M30: '30m',
  H1: '1H',
  H4: '4H',
  D1: '1D',
  W1: '1W',
  MN1: '1M',
} as const;

export const BAR_INTERVALS = Object.values(BAR_INTERVAL);
export type BarInterval = (typeof BAR_INTERVAL)[keyof typeof BAR_INTERVAL];

export type ProfitTimeRange = '30d' | '7d' | '90d' | 'all' | '15d';
export type TaskStatus = 'completed' | 'failed' | 'pending' | 'running';

// ==========================================
// Core Domain Types
// ==========================================

/** Monitor Status */
export type MonitorStatus = 'running' | 'stopped' | 'abnormal';

/** Basic Monitor Status (Subset of MonitorStatus) */
export type BasicMonitorStatus = 'running' | 'stopped';

/** Monitor Filter Status (Superset of MonitorStatus) */
export type MonitorFilterStatus = 'all' | 'running' | 'stopped';

/** Batch Status */
export type BatchStatus = 'completed' | 'failed' | 'idle' | 'pending' | 'running';

/** Pipeline Status */
export type PipelineStatus =
  | 'IDLE'
  | 'STARTING'
  | 'SELECTING'
  | 'FILTERING'
  | 'INITIALIZING_ASSET'
  | 'COLLECTING'
  | 'SYSTEM_PROMPT'
  | 'USER_PROMPT'
  | 'ANALYZING'
  | 'EXECUTING'
  | 'WAITING'
  | 'COMPLETED'
  | 'FAILED'
  | 'STOPPING';

/** Pipeline Status Data */
export interface PipelineStatusData {
  status: PipelineStatus;
  startTime: number;
  endTime?: number;
  currentAssetIndex: number;
  totalAssets: number;
  lastUpdateTime: number;
}

/** Batch Result Status (Subset of TaskStatus) */
export type BatchResultStatus = 'completed' | 'failed';

/** Regenerate Status */
export type RegenerateStatus = 'idle' | 'generating' | 'analyzing' | 'completed' | 'failed' | 'processing';

/** Risk Level */
export type RiskLevel = 'low' | 'medium' | 'high';

/** Health Status */
export type HealthStatus = 'healthy' | 'unhealthy';

/** Protocol */
export type Protocol = 'http' | 'https' | 'socks4' | 'socks5';

/** Sort Direction */
export type SortDirection = 'asc' | 'desc';

/** Timeframe (Alias for BarInterval) */
export type Timeframe = BarInterval;

/** Board Type */
export type BoardType = 'industry' | 'concept';

// ==========================================
// Trading & Order Types
// ==========================================

/** Order Type */
export type OrderType = 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT';

export const ORDER_SIDE = {
  BUY: 'buy',
  SELL: 'sell',
} as const;

/** Order Side */
export type OrderSide = (typeof ORDER_SIDE)[keyof typeof ORDER_SIDE];

export const POSITION_SIDE = {
  LONG: 'long',
  SHORT: 'short',
  NET: 'net',
} as const;

/** Position Side */
export type PositionSide = (typeof POSITION_SIDE)[keyof typeof POSITION_SIDE];

/** Position Action */
export type PositionAction = 'close' | 'hold' | 'reduce';

/** Position Filter */
export type PositionFilter = 'all' | 'long' | 'short' | 'net';

// ==========================================
// Market Data & Analysis Types
// ==========================================

/** Scanner Time Frame */
export type ScannerTimeFrame = '1D' | '1H' | '4H';

/** Stock Ranking Metric */
export type StockRanking = 'amount' | 'momentum' | 'rebound';

/** Price Source */
export type PriceSource = 'close' | 'open' | 'high' | 'low' | 'hl2' | 'hlc3' | 'ohlc4';

/** Break Type */
export type BreakType = 'resistance' | 'support';

/** Alert Type */
export type AlertType =
  | 'SURGE'
  | 'DROP'
  | 'NEW_COIN'
  | 'NEW_HIGH'
  | 'NEW_LOW'
  | 'FUNDING_RATE_HIGH'
  | 'POSITION_PROFIT'
  | 'POSITION_LOSS'
  | 'BREAK_RESISTANCE'
  | 'BREAK_SUPPORT'
  | 'VOLUME_SURGE'
  | 'POSITION_CHANGE'
  | 'KLINE_BUY_SIGNAL'
  | 'KLINE_SELL_SIGNAL'
  | 'RSI_OVERBOUGHT'
  | 'RSI_OVERSOLD'
  | 'RESEARCH_REPORT';

/** Scanner Signal Type */
export type ScannerSignalType = 'ACCUMULATION' | 'ABSORPTION' | 'NONE';

/**
 * Metrics Field Name
 */
export type MetricsFieldName =
  | 'price'
  | 'high24h'
  | 'low24h'
  | 'changePct24h'
  | 'ema20'
  | 'ema50'
  | 'rsi14'
  | 'cci14'
  | 'cmf14'
  | 'atr14'
  | 'macd'
  | 'macdHist'
  | 'bbPctB'
  | 'volume24h'
  | 'obv'
  | 'fundingRate'
  | 'resistance1'
  | 'resistance2'
  | 'support1'
  | 'support2';

/** Comparison Operator */
export type ComparisonOperator = '>=' | '<=' | '>' | '<' | '==' | '!=' | '=';

// ==========================================
// Strategy Types
// ==========================================

/** Strategy Category */
export type StrategyCategory = 'momentum' | 'volatility' | 'trend' | 'reversal';

/** Ranking Algorithm (Alias for StrategyCategory) */
export type RankingAlgorithm = StrategyCategory;

/** Strategy Code */
export type StrategyCode =
  | 'mean_reversion'
  | 'range_trading'
  | 'reversal'
  | 'false_breakout'
  | 'momentum_fade'
  | 'technical_prediction'
  | 'resistance_short'
  | 'breakdown_trend_short'
  | 'rsi_divergence_short'
  | 'volatility_squeeze_short'
  | 'ema_pullback_short'
  | 'distribution_flow_short'
  | 'bull_trap_short'
  | 'cci_extreme_short'
  | 'obv_exhaustion_short'
  | 'r_breaker'
  | 'dual_thrust'
  | 'aberration'
  | 'turtle_trading'
  | 'grid_trading'
  | 'escalator_trend'
  | 'custom'
  | 'ai_analysis'
  | 'macd_cross'
  | 'rsi_monitor'
  | 'future_tech_leaders'
  | 'undervalued_growth'
  | 'hot_stocks'
  | 'industry_leaders'
  | 'small_cap_active'
  | 'year_bottom_rebound_trend'
  | 'momentum_strong'
  | 'industrial_trend'
  | 'trend_follow'
  | 'volume_accumulation';

/** Strategy Version Filter */
export type StrategyVersionFilter = '2' | '3' | 'all';

/** Optimization Metric */
export type OptimizationMetric =
  | 'calmar'
  | 'return'
  | 'sharpe'
  | 'sortino'
  | 'net_profit'
  | 'sharpe_ratio'
  | 'win_rate'
  | 'profit_factor'
  | 'drawdown';

/** Optimization Method */
export type OptimizationMethod = 'bayesian' | 'genetic' | 'grid' | 'random';

// ==========================================
// Financial & Transaction Types
// ==========================================

/** Transaction Type */
export type TransactionType = 'deposit' | 'withdraw' | 'trade';

/** Transaction Status */
export type TransactionStatus = 'pending' | 'completed' | 'failed';

// ==========================================
// Backend Specific Enums (Kept for compatibility)
// ==========================================

/** Cross Direction (Watchlist/Alerts) */
export type CrossDirection = 'above' | 'below';

/** Volume Status (Analysis) */
export type VolumeStatus = 'High' | 'Low' | 'Normal' | 'Ultra-High';

/** Candle Pattern (Analysis) */
export type CandlePattern = 'Bearish' | 'Bullish' | 'Doji';

/** Trend Position (Analysis) */
export type TrendPosition = 'Bottom' | 'None' | 'Top';

/** HTTP Method */
export type HttpMethod = 'GET' | 'POST';

/** Log Format */
export type LogFormat = 'detailed' | 'minimal' | 'standard';

/** Execution Status */
export type ExecutionStatus = 'failed' | 'success';

/** Service Health Status */
export type ServiceHealth = 'degraded' | 'healthy' | 'unhealthy';

/** Market Phase (Momentum) */
export type MarketPhase = 'ABSORPTION' | 'ACCUMULATION' | 'NONE';

/** Trade Status */
export type TradeStatus = 'closed' | 'open';

/** Validation Type */
export type ValidationType = 'array' | 'boolean' | 'number' | 'object' | 'string';

/** Validation Source */
export type ValidationSource = 'body' | 'params' | 'query';

/** Backtest Entry Type */
export type EntryType = 'entry_long' | 'entry_short';

/** Backtest Add Position Type */
export type AddPositionType = 'add_long' | 'add_short';

/** Backtest Exit Type */
export type ExitType = 'exit_long' | 'exit_short' | 'stop_loss' | 'take_profit';

/** Moving Average Type */
export type MovingAverageType = 'EMA' | 'SMA' | 'WMA';

/** Reversal Type */
export type ReversalType = 'candlestick' | 'divergence' | 'indicator';

/** Momentum Indicator Type */
export type MomentumIndicatorType = 'MACD' | 'ROC' | 'RSI';

/** Stock Adjust Type */
export type StockAdjustType = 'hfq' | 'qfq';

/** Trend Direction */
export type TrendDirection = 'Down' | 'Up';

/** Position Change Type */
export type PositionChangeType = 'close' | 'decrease' | 'increase' | 'mixed' | 'open' | 'reverse';

/** Chinese Trend Direction */
export type TrendDirectionCN = '上涨' | '下跌';

/** Strategy Time Frame */
export type StrategyTimeFrame = BarInterval | '1m' | '7d' | '14d' | '30d';

/** Prediction Signal */
export type PredictionSignal = 'BEARISH' | 'BULLISH' | 'NEUTRAL';

/** HighLowAlertType */
export type HighLowAlertType = 'NEW_HIGH' | 'NEW_LOW';

/** Signal Type */
export type SignalType =
  | 'buy'
  | 'sell'
  | 'entry_long'
  | 'entry_short'
  | 'add_long'
  | 'add_short'
  | 'exit_long'
  | 'exit_short'
  | 'stop_loss'
  | 'take_profit';

/** Exit Reason */
export type ExitReason = 'sl' | 'tp' | 'signal' | 'time_exit' | 'end_of_period';

/** Batch Task Status */
export type BatchTaskStatus = TaskStatus;

/** Price Crossing Direction */
export type PriceCrossingDirection = 'UP' | 'DOWN';

/** Toast Type */
export type ToastType = 'success' | 'error' | 'info' | 'warning';

/** Quant Account Tab */
export type QuantAccountTab = 'overview' | 'calendar';

// ==========================================
// UI & System Types
// ==========================================

/** App Page */
export type AppPage =
  | 'dashboard'
  | 'home'
  | 'quant-account'
  | 'wallet'
  | 'deposit'
  | 'withdraw'
  | 'profit'
  | 'settings'
  | 'api-test'
  | 'link-generator'
  | 'tools'
  | 'price-alerts-history'
  | 'watchlist'
  | 'watchlist-self'
  | 'my-monitors'
  | 'signal-triggers'
  | 'kline-playback'
  | 'personal-center'
  | 'coin-metrics-analysis'
  | 'my-orders'
  | 'my-pending-orders'
  | 'user-list'
  | 'backtest'
  | 'strategy-square'
  | 'strategy-detail'
  | 'system-tasks'
  | 'system-prompts'
  | 'ai-model-management'
  | 'auto-trading-records'
  | 'signal-scanner'
  | 'historical-positions'
  | 'stock-monitors'
  | 'stock-dashboard'
  | 'stock-strategies'
  | 'stock-quant-portfolio'
  | 'stock-portfolio'
  | 'research-reports'
  | 'stock-signals';

/** User Role Value */
export type UserRoleValue = 'USER' | 'VIP' | 'ADMIN' | 'SUPER_ADMIN';

/** Date Format */
export type DateFormat = 'short' | 'detailed' | 'history';

/** Chat Role */
export type ChatRole = 'assistant' | 'system' | 'user';

/** Log Source */
export type LogSource = 'combined' | 'system' | 'user';

/** Log Export Format */
export type LogExportFormat = 'json' | 'markdown' | 'prompt';

/** Theme Mode */
export type ThemeMode = 'dark' | 'light';

/** Size Option */
export type SizeOption = 'lg' | 'md' | 'sm' | 'xl';

/** Button Size (Subset of SizeOption) */
export type ButtonSize = 'lg' | 'md' | 'sm';

/** Tag Color */
export type TagColor = 'blue' | 'green' | 'red';

/** Animation Variant */
export type AnimationVariant = 'fadeInDown' | 'fadeInLeft' | 'fadeInRight' | 'fadeInUp' | 'scaleIn' | 'slideIn';

/** Spinner Color */
export type SpinnerColor = 'cyan' | 'purple' | 'pink' | 'green';

/** TitleVariant */
export type TitleVariant = 'glow' | 'gradient' | 'neon';

/** ButtonVariant */
export type ButtonVariant = 'danger' | 'primary' | 'secondary' | 'success';

/** NeonColor */
export type NeonColor = 'blue' | 'cyan' | 'green' | 'pink' | 'purple';

/** AvatarSize */
export type AvatarSize = '2xl' | 'lg' | 'md' | 'sm' | 'xl';

/** AutoTradingActionFilter */
export type AutoTradingActionFilter = 'all' | 'close_position' | 'place_order';

/** AutoTradingStatusFilter */
export type AutoTradingStatusFilter = 'all' | 'failed' | 'success';

/** StrategyOperation */
export type StrategyOperation = 'create' | 'delete' | 'quantitative' | 'start' | 'stop';

/** SystemTaskTypeFilter */
export type SystemTaskTypeFilter =
  | 'all'
  | 'batch-backtest'
  | 'batch-optimize'
  | 'coin-analysis'
  | 'kline-monitor-rules'
  | 'kline-prompt'
  | 'order'
  | 'price-monitor'
  | 'signal-backtest'
  | 'signal-optimize'
  | 'stock-collect'
  | 'stock-analyze';

// ==========================================
// OKX Specific Enums
// ==========================================

export const OKX_TD_MODE = {
  CASH: 'cash',
  ISOLATED: 'isolated',
  CROSS: 'cross',
} as const;

/** Trade Mode */
export type TdMode = (typeof OKX_TD_MODE)[keyof typeof OKX_TD_MODE];

export const OKX_ORDER_TYPE = {
  MARKET: 'market',
  LIMIT: 'limit',
  POST_ONLY: 'post_only',
  FOK: 'fok',
  IOC: 'ioc',
  OPTIMAL_LIMIT_IOC: 'optimal_limit_ioc',
} as const;

/** OKX Order Type */
export type OkxOrderType = (typeof OKX_ORDER_TYPE)[keyof typeof OKX_ORDER_TYPE];

export const OKX_ALGO_ORDER_TYPE = {
  CONDITIONAL: 'conditional',
  OCO: 'oco',
  TRIGGER: 'trigger',
  MOVE_ORDER_STOP: 'move_order_stop',
  ICEBERG: 'iceberg',
  TWAP: 'twap',
} as const;

/** Algo Order Type */
export type AlgoOrderType = (typeof OKX_ALGO_ORDER_TYPE)[keyof typeof OKX_ALGO_ORDER_TYPE];
